import { For, Index, Show, createMemo, createSignal } from "solid-js"
import type { ExpertController } from "./expert-controller"
import type { Expert } from "./expert-types"
import { useLanguage } from "@/context/language"
import { Icon as IconV2 } from "@opencode-ai/ui/v2/icon"
import expertAvatar1 from "@/assets/self-develop/icon1.svg"
import expertAvatar2 from "@/assets/self-develop/icon2.svg"
import expertAvatar3 from "@/assets/self-develop/icon3.svg"
import expertAvatar4 from "@/assets/self-develop/icon4.svg"
import expertAvatar5 from "@/assets/self-develop/icon5.svg"
import "./expert.css"

const AVATAR_COLORS = ["purple", "teal", "amber", "blue", "pink", "default"] as const

function avatarColorClass(index: number) {
  return `expert-card-avatar--${AVATAR_COLORS[index % AVATAR_COLORS.length]}`
}

// 内置专家头像：前 5 个按顺序使用 icon1-5，从第 6 个起统一使用 icon5
// （同 new-session-view.tsx 里 expertIcons 的规则）
const expertAvatars = [expertAvatar1, expertAvatar2, expertAvatar3, expertAvatar4, expertAvatar5]
const expertAvatarFor = (index: number) =>
  index < expertAvatars.length ? expertAvatars[index] : expertAvatar5

/** 头像图标：内置专家按下标取 icon1-5，自定义专家（含新建时）统一使用 icon5 */
const avatarSrcFor = (expert: Expert | undefined, index: number) =>
  expert?.builtin ? expertAvatarFor(index) : expertAvatar5

/** 卡片 / 弹框统一头像：同一个专家渲染同一个图标 + 同色圆形底，
 * 保证预览、编辑弹框与卡片右侧那张卡片的头像完全一致。 */
function ExpertAvatar(props: { expert?: Expert; index: number; size?: "card" | "detail" }) {
  const index = () => Math.max(0, props.index)
  const sizeClass = props.size === "detail" ? "expert-detail-avatar" : ""
  return (
    <div class={`expert-card-avatar ${avatarColorClass(index())} ${sizeClass}`.trim()}>
      <img class="expert-card-avatar-img" src={avatarSrcFor(props.expert, index())} alt="" />
    </div>
  )
}

function ExpertCard(props: {
  expert: Expert
  index: number
  onPreview: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  const e = props.expert
  const tags = e.tags ?? []
  const skillNames = () => (e.skill_options ?? []).map((option) => option.label).join("、")
  return (
    <div class="expert-card" onClick={props.onPreview}>
      <div class="expert-card-header">
        <ExpertAvatar expert={e} index={props.index} />
        <div class="expert-card-info">
          <div class="expert-card-name-row">
            <span class="expert-card-name" title={e.name}>
              {e.name}
            </span>
            <Show when={e.builtin}>
              <span class="expert-card-badge">内置</span>
            </Show>
          </div>
          <Show when={e.alias}>
            <div class="expert-card-alias" title={e.alias}>
              {e.alias}
            </div>
          </Show>
        </div>
      </div>

      <div class="expert-card-body">
        <p class="expert-card-desc">{e.description || "暂无简介"}</p>
        <Show when={tags.length > 0}>
          <div class="expert-card-tags">
            <For each={tags.slice(0, 2)}>
              {(tag) => (
                <span class="expert-card-tag" title={tag}>
                  {tag}
                </span>
              )}
            </For>
            <Show when={tags.length > 2}>
              <span class="expert-card-tag expert-card-tag--more" title={tags.join("、")}>
                +{tags.length - 2}
              </span>
            </Show>
          </div>
        </Show>
      </div>

      <div class="expert-card-footer">
        <span class="expert-card-skill" title={skillNames() || "未绑定技能"}>
          {skillNames() || "未绑定技能"}
        </span>
        <div class="expert-card-actions">
          <div class="expert-card-icons">
            <button
              type="button"
              class="expert-icon-btn"
              title="预览"
              aria-label="预览"
              onClick={(ev) => {
                ev.stopPropagation()
                props.onPreview()
              }}
            >
              <IconV2 name="eye" size="small" />
            </button>
            <button
              type="button"
              class="expert-icon-btn"
              title="编辑"
              aria-label="编辑"
              onClick={(ev) => {
                ev.stopPropagation()
                props.onEdit()
              }}
            >
              <IconV2 name="edit" size="small" />
            </button>
            <Show when={!e.builtin}>
              <button
                type="button"
                class="expert-icon-btn expert-icon-btn--danger"
                title="删除"
                aria-label="删除"
                onClick={(ev) => {
                  ev.stopPropagation()
                  props.onDelete()
                }}
              >
                <IconV2 name="trash" size="small" />
              </button>
            </Show>
          </div>
        </div>
      </div>
    </div>
  )
}

function ExpertDetailModal(props: { c: ExpertController; index: number }) {
  const c = props.c
  const expert = c.selectedExpert

  return (
    <Show when={c.selectedExpertId()}>
      <div class="expert-detail-overlay" onClick={() => c.setSelectedExpertId(null)}>
        <div class="expert-detail-dialog" onClick={(e) => e.stopPropagation()}>
          <Show when={expert()}>
            {(e) => (
              <>
                <div class="expert-detail-header">
                  <ExpertAvatar expert={e()} index={props.index} size="detail" />
                  <div class="expert-detail-title-area">
                    <h2 class="expert-detail-name">{e().name}</h2>
                    <Show when={e().alias}>
                      <span class="expert-detail-alias">{e().alias}</span>
                    </Show>
                    <Show when={(e().tags?.length ?? 0) > 0}>
                      <div class="expert-detail-tags">
                        <For each={e().tags ?? []}>{(tag) => <span class="expert-detail-tag">{tag}</span>}</For>
                      </div>
                    </Show>
                  </div>
                  <button class="expert-detail-close" onClick={() => c.setSelectedExpertId(null)}>
                    ✕
                  </button>
                </div>
                <div class="expert-detail-body">
                  <div class="expert-detail-section">
                    <h3 class="expert-detail-section-title">专家简介</h3>
                    <p class="expert-detail-description">{e().description || "暂无简介"}</p>
                  </div>
                  <Show when={(e().skill_options?.length ?? 0) > 0}>
                    <div class="expert-detail-section">
                      <h3 class="expert-detail-section-title">绑定技能</h3>
                      <div class="expert-detail-skill-groups">
                        <For each={e().skill_options}>
                          {(option) => (
                            <div class="expert-detail-skill-group">
                              <span class="expert-detail-skill-badge">{option.label}</span>
                              <Show when={(option.children?.length ?? 0) > 0}>
                                <div class="expert-detail-skill-children">
                                  <For each={option.children}>
                                    {(child) => (
                                      <span class="expert-detail-skill-badge expert-detail-skill-badge--child">
                                        {child.label}
                                      </span>
                                    )}
                                  </For>
                                </div>
                              </Show>
                            </div>
                          )}
                        </For>
                      </div>
                    </div>
                  </Show>
                  <div class="expert-detail-section">
                    <h3 class="expert-detail-section-title">专家帮你做</h3>
                    <Show
                      when={(e().preset_questions?.length ?? 0) > 0}
                      fallback={<p class="expert-detail-empty">暂无预置问题</p>}
                    >
                      <div class="expert-detail-questions">
                        <For each={e().preset_questions ?? []}>
                          {(question) => (
                            <button class="expert-detail-question" onClick={() => c.startConversation(e(), question)}>
                              <span class="expert-detail-question-text">{question}</span>
                              <span class="expert-detail-question-arrow">→</span>
                            </button>
                          )}
                        </For>
                      </div>
                    </Show>
                  </div>
                </div>
              </>
            )}
          </Show>
        </div>
      </div>
    </Show>
  )
}

function ExpertForm(props: { c: ExpertController; index: number }) {
  const c = props.c
  const language = useLanguage()
  // 编辑时按被编辑的专家判断是否内置；新建时还没有专家，按自定义处理
  const editingExpert = () => c.allExperts().find((item) => item.id === c.editingExpertId())
  const isValid = () =>
    c.formData.name.trim().length > 0 &&
    c.formData.skill_options.every(
      (option) =>
        option.id.trim().length > 0 &&
        option.label.trim().length > 0 &&
        (option.children ?? []).every((child) => child.id.trim().length > 0 && child.label.trim().length > 0),
    )

  return (
    <div class="expert-form-overlay" onClick={c.closeForm}>
      <div class="expert-form-dialog" onClick={(e) => e.stopPropagation()}>
        <div class="expert-form-header">
          <span class="expert-form-title">{c.editingExpertId() ? "编辑专家" : "创建专家"}</span>
          <button class="expert-form-close" onClick={c.closeForm}>
            ✕
          </button>
        </div>
        <div class="expert-form-body">
          <div class="expert-form-field">
            <label class="expert-form-label">专家头像</label>
            {/* 头像框复用输入框样式（expert-form-input），框内只渲染卡片使用的 svg 头像；
                该头像由专家是否内置（+ 内置时的列表位置）决定，与卡片、预览弹框是同一个 */}
            <div class="expert-form-input expert-form-avatar-box">
              <img class="expert-form-avatar-img" src={avatarSrcFor(editingExpert(), props.index)} alt="" />
            </div>
          </div>
          <div class="expert-form-field">
            <label class="expert-form-label">专家名称 *</label>
            <input
              class="expert-form-input"
              placeholder="如：模型攻防与评测专家"
              value={c.formData.name}
              onInput={(e) => c.setFormData("name", e.currentTarget.value)}
            />
          </div>
          <div class="expert-form-field">
            <label class="expert-form-label">专家别名</label>
            <input
              class="expert-form-input"
              placeholder="如：Model Security Expert"
              value={c.formData.alias}
              onInput={(e) => c.setFormData("alias", e.currentTarget.value)}
            />
          </div>
          <div class="expert-form-field">
            <label class="expert-form-label">专家简介</label>
            <textarea
              class="expert-form-textarea"
              placeholder="描述专家的专业领域与能力范围"
              value={c.formData.description}
              onInput={(e) => c.setFormData("description", e.currentTarget.value)}
            />
          </div>
          <div class="expert-form-field">
            <label class="expert-form-label">能力标签</label>
            <input
              class="expert-form-input"
              placeholder="逗号分隔，如：模型评测, 攻防演练"
              value={c.formData.tags}
              onInput={(e) => c.setFormData("tags", e.currentTarget.value)}
            />
          </div>
          <div class="expert-form-field">
            <label class="expert-form-label">绑定 Skill</label>
            <p class="expert-form-hint">
              一级项不填「命令前缀」、并在其下添加子技能时，首页会变成「先选类别、再选技能」的两级选择（如情报专家）。
            </p>
            <div class="expert-form-skills">
              <Index each={c.formData.skill_options}>
                {(option, index) => (
                  <div class="expert-form-skill-group">
                    <div class="expert-form-skill-row">
                      <input
                        class="expert-form-input expert-form-skill-icon"
                        aria-label={`Skill ${index + 1} 图标`}
                        placeholder="图标"
                        value={option().icon ?? ""}
                        onInput={(event) => c.setSkillOption(index, "icon", event.currentTarget.value)}
                      />
                      <input
                        class="expert-form-input"
                        aria-label={`Skill ${index + 1} 展示名称`}
                        placeholder="展示名称，如：新闻鉴伪"
                        value={option().label}
                        onInput={(event) => c.setSkillOption(index, "label", event.currentTarget.value)}
                      />
                      <input
                        class="expert-form-input"
                        aria-label={`Skill ${index + 1} ID`}
                        placeholder="Skill ID"
                        value={option().id}
                        onInput={(event) => c.setSkillOption(index, "id", event.currentTarget.value)}
                      />
                      <input
                        class="expert-form-input"
                        aria-label={`Skill ${index + 1} 命令前缀`}
                        placeholder="命令前缀，如：/tasks "
                        value={option().prefix ?? ""}
                        onInput={(event) => c.setSkillOption(index, "prefix", event.currentTarget.value)}
                      />
                      <div class="expert-form-skill-row-actions">
                        <button
                          type="button"
                          class="expert-form-skill-add-child"
                          title="在该项下添加子技能（添加后该项变成可展开的类别）"
                          aria-label={`为 Skill ${index + 1} 添加子技能`}
                          onClick={() => c.addChildSkillOption(index)}
                        >
                          ＋
                        </button>
                        <button
                          type="button"
                          class="expert-form-question-remove"
                          aria-label={`删除 Skill ${index + 1}`}
                          onClick={() => c.removeSkillOption(index)}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                    <Index each={option().children ?? []}>
                      {(child, childIndex) => (
                        <>
                        <div class="expert-form-skill-row expert-form-skill-child">
                          <input
                            class="expert-form-input expert-form-skill-icon"
                            aria-label={`子技能 ${childIndex + 1} 图标`}
                            placeholder="图标"
                            value={child().icon ?? ""}
                            onInput={(event) =>
                              c.setChildSkillOption(index, childIndex, "icon", event.currentTarget.value)
                            }
                          />
                          <input
                            class="expert-form-input"
                            aria-label={`子技能 ${childIndex + 1} 展示名称`}
                            placeholder="展示名称，如：政治人物"
                            value={child().label}
                            onInput={(event) =>
                              c.setChildSkillOption(index, childIndex, "label", event.currentTarget.value)
                            }
                          />
                          <input
                            class="expert-form-input"
                            aria-label={`子技能 ${childIndex + 1} ID`}
                            placeholder="Skill ID"
                            value={child().id}
                            onInput={(event) => c.setChildSkillOption(index, childIndex, "id", event.currentTarget.value)}
                          />
                          <input
                            class="expert-form-input"
                            aria-label={`子技能 ${childIndex + 1} 命令前缀`}
                            placeholder="命令前缀，如：/us-intel "
                            value={child().prefix ?? ""}
                            onInput={(event) =>
                              c.setChildSkillOption(index, childIndex, "prefix", event.currentTarget.value)
                            }
                          />
                          <div class="expert-form-skill-row-actions">
                            <button
                              type="button"
                              class="expert-form-question-remove"
                              aria-label={`删除子技能 ${childIndex + 1}`}
                              onClick={() => c.removeChildSkillOption(index, childIndex)}
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                        {/* 子技能自带预置问题：选中该子技能时优先展示 */}
                        <div
                          class="expert-form-skill-questions"
                          style={{ "margin-left": "30px", "margin-top": "6px" }}
                        >
                          <Index each={child().preset_questions ?? []}>
                            {(question, qIndex) => (
                              <div class="expert-form-question-row">
                                <input
                                  class="expert-form-input"
                                  placeholder="子技能预置问题"
                                  value={question()}
                                  onInput={(e) =>
                                    c.setChildPresetQuestion(index, childIndex, qIndex, e.currentTarget.value)
                                  }
                                />
                                <Show when={(child().preset_questions ?? []).length > 1}>
                                  <button
                                    class="expert-form-question-remove"
                                    onClick={() => c.removeChildPresetQuestion(index, childIndex, qIndex)}
                                  >
                                    ✕
                                  </button>
                                </Show>
                              </div>
                            )}
                          </Index>
                          <button
                            type="button"
                            class="expert-form-question-add"
                            onClick={() => c.addChildPresetQuestion(index, childIndex)}
                          >
                            + 添加问题
                          </button>
                        </div>
                        </>
                      )}
                    </Index>
                    {/* 一级叶子技能自带预置问题（类别项有 children 不渲染：点击只展开不选中） */}
                    <Show when={(option().children?.length ?? 0) === 0}>
                      <div
                        class="expert-form-skill-questions"
                        style={{ "margin-left": "30px", "margin-top": "6px" }}
                      >
                        <Index each={option().preset_questions ?? []}>
                          {(question, qIndex) => (
                            <div class="expert-form-question-row">
                              <input
                                class="expert-form-input"
                                placeholder="该技能的预置问题"
                                value={question()}
                                onInput={(e) =>
                                  c.setSkillPresetQuestion(index, qIndex, e.currentTarget.value)
                                }
                              />
                              <Show when={(option().preset_questions ?? []).length > 1}>
                                <button
                                  class="expert-form-question-remove"
                                  onClick={() => c.removeSkillPresetQuestion(index, qIndex)}
                                >
                                  ✕
                                </button>
                              </Show>
                            </div>
                          )}
                        </Index>
                        <button
                          type="button"
                          class="expert-form-question-add"
                          onClick={() => c.addSkillPresetQuestion(index)}
                        >
                          + 添加问题
                        </button>
                      </div>
                    </Show>
                  </div>
                )}
              </Index>
              <button type="button" class="expert-form-question-add" onClick={c.addSkillOption}>
                + 添加 Skill
              </button>
            </div>
          </div>
          <div class="expert-form-field">
            <label class="expert-form-label">预置推荐问题</label>
            <div class="expert-form-questions">
              <Index each={c.formData.preset_questions}>
                {(question, index) => (
                  <div class="expert-form-question-row">
                    <input
                      class="expert-form-input"
                      placeholder="如：帮我创建基础评测任务"
                      value={question()}
                      onInput={(e) => c.setPresetQuestion(index, e.currentTarget.value)}
                    />
                    <Show when={c.formData.preset_questions.length > 1}>
                      <button class="expert-form-question-remove" onClick={() => c.removePresetQuestion(index)}>
                        ✕
                      </button>
                    </Show>
                  </div>
                )}
              </Index>
              <button class="expert-form-question-add" onClick={c.addPresetQuestion}>
                + 添加问题
              </button>
            </div>
          </div>
          <label class="expert-form-toggle">
            <span class="expert-form-label">{language.t("expert.form.showOnHome")}</span>
            <input
              type="checkbox"
              checked={c.formData.show_on_home}
              onChange={(event) => c.setFormData("show_on_home", event.currentTarget.checked)}
            />
          </label>
        </div>
        <Show when={c.error()}>
          <div class="expert-form-error">{c.error()}</div>
        </Show>
        <div class="expert-form-actions">
          <button class="expert-form-btn-ghost" onClick={c.closeForm}>
            取消
          </button>
          <button class="expert-form-btn-primary" disabled={!isValid() || c.loading()} onClick={c.saveForm}>
            {c.loading() ? "保存中..." : "保存"}
          </button>
        </div>
      </div>
    </div>
  )
}

function ConfirmDelete(props: { c: ExpertController }) {
  const c = props.c
  const expert = () => c.allExperts().find((e) => e.id === c.confirmDeleteId())

  return (
    <Show when={c.confirmDeleteId()}>
      <div class="expert-confirm-overlay" onClick={() => c.setConfirmDeleteId(null)}>
        <div class="expert-confirm-dialog" onClick={(e) => e.stopPropagation()}>
          <p class="expert-confirm-text">确认删除专家「{expert()?.name ?? ""}」？此操作不可撤销。</p>
          <div class="expert-confirm-actions">
            <button class="expert-form-btn-ghost" onClick={() => c.setConfirmDeleteId(null)}>
              取消
            </button>
            <button
              class="expert-confirm-btn-danger"
              onClick={() => {
                c.deleteExpert(c.confirmDeleteId()!)
                c.setConfirmDeleteId(null)
              }}
            >
              确认删除
            </button>
          </div>
        </div>
      </div>
    </Show>
  )
}

export function ExpertView(props: { controller: ExpertController }) {
  const c = props.controller
  const [filter, setFilter] = createSignal<"all" | "builtin" | "custom">("all")

  const stats = createMemo(() => {
    const all = c.allExperts()
    return {
      total: all.length,
      builtin: all.filter((e) => e.builtin).length,
      custom: all.filter((e) => !e.builtin).length,
      withSkill: all.filter((e) => (e.skill_options?.length ?? 0) > 0).length,
    }
  })

  const filteredExperts = createMemo(() => {
    const list = c.experts()
    const f = filter()
    if (f === "builtin") return list.filter((e) => e.builtin)
    if (f === "custom") return list.filter((e) => !e.builtin)
    return list
  })

  // 弹框里的头像要跟对应卡片一致：按卡片所在列表的下标取同一个图标/配色
  const cardIndexOf = (id: string | null) => (id ? filteredExperts().findIndex((e) => e.id === id) : -1)
  const detailIndex = createMemo(() => Math.max(0, cardIndexOf(c.selectedExpertId())))
  const formIndex = createMemo(() => {
    const id = c.editingExpertId()
    // 新建时用「即将追加到的位置」预览，卡片本身也按这个下标取头像
    return id ? Math.max(0, cardIndexOf(id)) : c.experts().length
  })

  return (
    <div class="expert-root">
      {/* Stats bar */}
      <div class="expert-stats">
        <div class="expert-stat-item">
          <span class="expert-stat-label">专家总数</span>
          <span class="expert-stat-value">{stats().total}</span>
        </div>
        <div class="expert-stat-item">
          <span class="expert-stat-label">内置专家</span>
          <span class="expert-stat-value">{stats().builtin}</span>
        </div>
        <div class="expert-stat-item">
          <span class="expert-stat-label">自定义</span>
          <span class="expert-stat-value">{stats().custom}</span>
        </div>
        <div class="expert-stat-item">
          <span class="expert-stat-label">已绑定技能</span>
          <span class="expert-stat-value">{stats().withSkill}</span>
        </div>
      </div>

      {/* Header */}
      <div class="expert-header">
        <div class="expert-header-left">
          <div class="expert-title">专家</div>
          {/* <p class="expert-subtitle">配置和管理专家，绑定技能能力与预置推荐问题</p> */}
        </div>
      </div>

      {/* Filter chips */}
      <div class="expert-filters">
        <div class="expert-filter-group">
          <button
            class="expert-filter-chip"
            classList={{ "expert-filter-chip--active": filter() === "all" }}
            onClick={() => setFilter("all")}
          >
            全部
            <span class="expert-filter-count">{stats().total}</span>
          </button>
          <button
            class="expert-filter-chip"
            classList={{ "expert-filter-chip--active": filter() === "builtin" }}
            onClick={() => setFilter("builtin")}
          >
            内置
            <span class="expert-filter-count">{stats().builtin}</span>
          </button>
          <button
            class="expert-filter-chip"
            classList={{ "expert-filter-chip--active": filter() === "custom" }}
            onClick={() => setFilter("custom")}
          >
            自定义
            <span class="expert-filter-count">{stats().custom}</span>
          </button>
        </div>
      </div>

      {/* Card grid */}
      <div class="expert-grid">
        <For each={filteredExperts()}>
          {(expert, index) => (
            <ExpertCard
              expert={expert}
              index={index()}
              onPreview={() => c.previewExpert(expert)}
              onEdit={() => c.openEdit(expert, avatarSrcFor(expert, index()))}
              onDelete={() => c.setConfirmDeleteId(expert.id)}
            />
          )}
        </For>

        {/* Add card placeholder */}
        <div class="expert-card-add" onClick={() => c.openCreate()}>
          <span class="expert-card-add-icon">+</span>
          <span class="expert-card-add-text">添加自定义专家</span>
        </div>
      </div>

      <Show when={c.experts().length === 0}>
        <div class="expert-empty">
          <span>暂无专家，点击右上角“创建专家”添加</span>
        </div>
      </Show>

      <Show when={c.showForm()}>
        <ExpertForm c={c} index={formIndex()} />
      </Show>

      <ExpertDetailModal c={c} index={detailIndex()} />

      <ConfirmDelete c={c} />

      <Show when={c.error()}>
        <div class="expert-error-toast">{c.error()}</div>
      </Show>
    </div>
  )
}
